/**
 * Validation Badge — shows response quality indicator.
 *
 * Green checkmark for clean responses, amber warning with expandable
 * list for responses that triggered validation checks.
 */

import { useState, useCallback } from 'react';
import type { ValidationResult } from '@skjv/common';

interface ValidationBadgeProps {
  validation: ValidationResult;
}

export function ValidationBadge({ validation }: ValidationBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggle = useCallback(() => setIsExpanded((v) => !v), []);

  const isClean = validation.warnings.length === 0;
  const pct = Math.round(validation.score * 100);

  return (
    <div className="validation-badge">
      <button className="validation-badge-toggle" onClick={toggle}>
        <span className={`validation-dot ${isClean ? 'validation-dot-pass' : 'validation-dot-warn'}`} />
        <span className="validation-score">
          {isClean ? 'Verified' : `${pct}%`}
        </span>
        {!isClean && (
          <span className="validation-count">
            {validation.warnings.length} {validation.warnings.length === 1 ? 'issue' : 'issues'}
          </span>
        )}
      </button>

      {isExpanded && validation.warnings.length > 0 && (
        <ul className="validation-warning-list">
          {validation.warnings.map((w, i) => (
            <li key={i} className="validation-warning-item">
              <span className="validation-warning-code">{w.code}</span>
              <span className="validation-warning-msg">{w.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
